import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { GoogleSignInButton } from "@/auth/GoogleSignInButton";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function LoginPage() {
  const { isAuthenticated, ready, login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (ready && isAuthenticated) return <Navigate to={from} replace />;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      navigate(from, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Sikertelen bejelentkezés.");
    } finally {
      setBusy(false);
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(() => login(userName, password));
  };

  return (
    <div className="app-shell grid min-h-dvh place-items-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <img
            src="/ziccer-logo.png"
            alt="Ziccer"
            className="mb-3 size-24 rounded-2xl shadow-lg shadow-primary/20 ring-1 ring-primary/30"
          />
          <CardTitle className="text-2xl font-bold tracking-tight">
            Ziccer<span className="text-primary">.</span>
          </CardTitle>
          <CardDescription>Jelentkezz be a tippeléshez</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="userName">Felhasználónév vagy e-mail</Label>
              <Input
                id="userName"
                autoComplete="username"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Jelszó</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              Belépés
            </Button>
          </form>

          <GoogleSignInButtonRow onGoogle={(t) => run(() => loginWithGoogle(t))} disabled={busy} />

          <p className="text-center text-sm text-muted-foreground">
            Még nincs fiókod?{" "}
            <Link to="/regisztracio" className="font-medium text-primary hover:underline">
              Regisztráció
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function GoogleSignInButtonRow({ onGoogle, disabled }: { onGoogle: (idToken: string) => void; disabled: boolean }) {
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        vagy
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="flex justify-center">
        <GoogleSignInButton onCredential={onGoogle} disabled={disabled} />
      </div>
    </div>
  );
}
